import { useState, useEffect } from "react";
import dayjs from "dayjs";

/**
 * Calculate elapsed time string from a start time to now.
 * Returns format "H:MM:SS" or "M:SS".
 */
const calculateElapsedTime = (startTime: string): string => {
  const start = dayjs(startTime);
  const now = dayjs();
  const diffSeconds = now.diff(start, "second");

  const hours = Math.floor(diffSeconds / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = diffSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

/**
 * Hook that returns a live-updating elapsed time string.
 * Updates every second while startTime is defined.
 */
export const useElapsedTime = (startTime: string | undefined): string => {
  const [elapsedTime, setElapsedTime] = useState<string>(() => {
    if (!startTime) return "--:--";
    return calculateElapsedTime(startTime);
  });

  useEffect(() => {
    if (!startTime) {
      setElapsedTime("--:--");
      return;
    }

    setElapsedTime(calculateElapsedTime(startTime));

    const interval = setInterval(() => {
      setElapsedTime(calculateElapsedTime(startTime));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  return elapsedTime;
};